import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { CACHE_KEY_PHARMAKON_PATIENT_LIST } from 'src/app/hooks/data/_keys';
import { useBffClient } from 'src/app/hooks/data/useBffClient';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import type { PatientDetails } from 'src/app/types';

// TODO: Remove the unnecessary arguments from all callees
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const usePatientList = (_UNUSED_accountIdArgument?: number, _UNUSED_enabledArgument?: boolean) => {
  const authClient = useBffClient();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';

  return useQuery<PatientDetails[], AxiosError>(
    [CACHE_KEY_PHARMAKON_PATIENT_LIST, accountId],
    () => authClient.get(`accounts/${accountId}/patients`).then((resp: { data: PatientDetails[] }) => resp.data ?? []),
    {
      enabled: user?.userId !== undefined,
    }
  );
};

export const usePrimaryPatient = () => {
  const { data: patientList, ...otherArgs } = usePatientList();
  return { data: patientList?.find(patient => patient.isPrimary) ?? null, ...otherArgs };
};

interface PatientUpdateParams {
  allergies: string;
  medicalConditions: string;
  otherMedications: string;
  birthdate: string;
}

export const usePatientUpdate = (id: number, accountId: number) => {
  const authClient = useBffClient();
  const queryClient = useQueryClient();

  return useMutation<PatientDetails, AxiosError, Partial<PatientUpdateParams>>(
    ({ allergies, medicalConditions, otherMedications, birthdate }) =>
      authClient
        .patch(
          `accounts/${accountId}/patients/${id}`, // Why does this endpoint required accountId...?
          {
            ...(allergies !== undefined && { allergies }),
            ...(medicalConditions !== undefined && { medicalConditions }),
            ...(otherMedications !== undefined && { otherMedications }),
            ...(birthdate !== undefined && { birthdate }),
          },

          { headers: { 'Content-Type': 'application/json' } }
        )
        .then((resp: { data: PatientDetails }) => resp.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CACHE_KEY_PHARMAKON_PATIENT_LIST]);
      },
    }
  );
};
